import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { fetchKnm } from 'APIPackage/KnmModule/services/knmApi';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

interface ILinkedPassportInfo {
  isPrescriptionCheck: boolean;
  isCreatedAfterSetting: boolean;
}

interface IUseLinkedPassportValidation {
  linkPassportId?: number;
  settings?: IAdminSetting[];
  isPrescriptionCheckReason: boolean;
  isImpossibilityOfPrevious: boolean;
  isEnablePrescriptionCheck: boolean;
}

/**
 * Определяет, нужно ли отключать валидацию ФОТ + КНО + ВК для требований, подтянутых из связанного паспорта.
 *
 * Возвращает `undefined`, пока основание связанного паспорта ещё не загружено — потребитель может
 * не запускать валидацию до этого момента.
 */
export function useLinkedPassportValidation({
  linkPassportId,
  settings,
  isPrescriptionCheckReason,
  isImpossibilityOfPrevious,
  isEnablePrescriptionCheck,
}: IUseLinkedPassportValidation): boolean | undefined {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [linkedPassportInfo, setLinkedPassportInfo] = useState<ILinkedPassportInfo | undefined>();

  useEffect(() => {
    // Сбрасываем данные предыдущей ссылки, чтобы не считать по устаревшему паспорту
    setLinkedPassportInfo(undefined);

    if (!linkPassportId) return;

    let isLatest = true;

    const loadPassport = async () => {
      try {
        const passportData: IKnm = await fetchKnm(linkPassportId);

        if (!isLatest) return;

        const reasons = passportData.knmErknm?.reasons ?? passportData.pmErknm?.reasons;
        const reason = reasons?.find(e => e?.reason?.type)?.reason;

        setLinkedPassportInfo({
          isPrescriptionCheck: reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK,
          isCreatedAfterSetting: isLaterThanAdminSetting(
            passportData.created,
            settings,
            AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
          ),
        });
      } catch (err) {
        if (!isLatest) return;

        // В случае ошибки проставляем результат по умолчанию в false, для снятия блокировки валидации
        setLinkedPassportInfo({ isPrescriptionCheck: false, isCreatedAfterSetting: false });

        dispatch(
          addNoticeAction({
            renderContent: 'Не удалось загрузить связанный паспорт для проверки основания',
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.ERKNM_LINKS,
          }),
        );
      }
    };

    loadPassport();

    return () => {
      isLatest = false;
    };
  }, [linkPassportId, dispatch, settings]);

  return useMemo(() => {
    // Если нет ссылки на паспорт, то ничего рассчитывать не нужно
    if (!linkPassportId) return false;

    // Пока идет загрузка данных значение = undefined
    if (!linkedPassportInfo) return undefined;

    return (
      (isPrescriptionCheckReason && isEnablePrescriptionCheck) ||
      (isImpossibilityOfPrevious && linkedPassportInfo.isPrescriptionCheck && linkedPassportInfo.isCreatedAfterSetting)
    );
  }, [
    linkPassportId,
    linkedPassportInfo,
    isPrescriptionCheckReason,
    isImpossibilityOfPrevious,
    isEnablePrescriptionCheck,
  ]);
}
